import React, { useState } from "react";
import { Col, Row } from "react-bootstrap";
import * as i18n from "util/i18n";
import MainContainer from "pages/_components/MainContainer";
import Button from "pages/_components/Button";
import { TransactionCard, TransactionHeader } from "pages/_components/TransactionCard";
import { ResponsiveHeader } from "pages/_components/header";
import useReactPrint from "pages/_components/Print/useReactToPrint";
import Notification from "pages/_components/Notification";
import { actions as echeckActions } from "reducers/echecks";
import { useTransaction } from "hooks/useTransactions";
import { useDispatch } from "react-redux";
import { bool } from "prop-types";
import { EcheqActions } from "pages/echeck/constants";
import TransactionSummaryBody from "pages/echeck/EcheckActions/components/TransactionSummaryBody";
import { useGetAccounts } from "hooks/useAccounts";

const TransactionPage = ({ isDesktop }) => {
    const dispatch = useDispatch();
    const [showMoreData, setShowMoreData] = useState(!isDesktop);
    const { accounts, fetching } = useGetAccounts();
    const confirmRoute = `/echecks/action/transaction/confirm`;

    const getTitleHeader = () => {
        if (transaction?.data?.action === EcheqActions.ADMIT || transaction?.data?.action === EcheqActions.REPUDIATE) {
            const echeq = transaction?.data?.echeqList[0];
            if (echeq.total_endosos > 0) {
                return `activities.echeq.${transaction.data.action.toLowerCase()}.endorse.action`;
            }
        }

        return `activities.echeq.${transaction?.data?.action?.toLowerCase()}.action`;
    };

    const formatTransactionEcheck = (data, idTransaction) => {
        const echeqData = {
            action: data.action,
            echeqList: data.echeqList,
            specialAttribute: data.specialAttribute,
            productId: data.productId,
            idTransaction,
            cancelTransaction: false,
            accountDescription: data.accountDescription,
        };

        dispatch(echeckActions.setEcheqsSpecialAttribute(data.specialAttribute));
        dispatch(echeckActions.setEcheqsProductId(data.productId));
        dispatch(echeckActions.setEchecksToDoActions(echeqData));

        return { ...echeqData };
    };

    const { isLoading, transaction, handleSignTransaction, showSignButton, push, id, userId, status } = useTransaction(
        confirmRoute,
        echeckActions.setEchecksToDoActions,
        echeckActions.cleanEcheckTicket,
        formatTransactionEcheck,
    );

    const { componentRef, handlePrint, mobileRef, handleShareMobile } = useReactPrint(
        getTitleHeader(),
        id,
        "/echecks/action/transaction",
    );

    const { isBackoffice, isFinished, isPending, isFailed, isCancelled } = status;

    const cancelTransaction = () => {
        const { data, idTransaction } = transaction;
        const echeqData = {
            action: data.action,
            echeqList: data.echeqList,
            specialAttribute: data.specialAttribute,
            productId: data.productId,
            idTransaction,
            cancelTransaction: true,
            userId,
            accountDescription: data.accountDescription,
        };

        dispatch(echeckActions.setEcheqsSpecialAttribute(data.specialAttribute));
        dispatch(echeckActions.setEcheqsProductId(data.productId));
        dispatch(echeckActions.setEchecksToDoActions(echeqData));

        dispatch(push(confirmRoute));
    };

    const isReady = !isLoading && !fetching;

    const handleDownload = async () => {
        await setShowMoreData(true);
        return handlePrint();
    };

    const renderBackToHomeButton = (isPrimary, offset) => (
        <Col xs={12} md={4} mdOffset={offset}>
            <Button
                onClick={() => dispatch(push("/desktop"))}
                label="forms.transaction.ticket.actions.backToHome"
                className={`btn${!isPrimary ? " btn-outline" : " btn-primary"}`}
            />
        </Col>
    );
    const renderDownloadButton = (isPrimary, offset) => (
        <Col xs={12} md={4} mdOffset={offset}>
            <Button
                onClick={handleDownload}
                image="images/figma/icon/download.svg"
                label="global.download"
                className={`btn${!isPrimary ? " btn-outline" : " btn-primary"}`}
            />
        </Col>
    );
    const renderCancelButton = (isPrimary, offset) => (
        <Col xs={12} md={4} mdOffset={offset}>
            <Button
                onClick={cancelTransaction}
                label="lotPayment.cancel"
                className={`btn${!isPrimary ? " btn-outline" : " btn-primary"}`}
            />
        </Col>
    );

    const renderSignButton = (isPrimary, offset) => (
        <Col xs={12} md={4} mdOffset={offset}>
            <Button
                onClick={handleSignTransaction}
                label="forms.signTransaction.link"
                className={`btn${!isPrimary ? " btn-outline" : " btn-primary"}`}
            />
        </Col>
    );

    return (
        <>
            <Notification scopeToShow="/echecks/action/transaction" />
            {!isBackoffice && <ResponsiveHeader ticket metaTitle={getTitleHeader()} />}
            <MainContainer showLoader={!isReady}>
                <div className="container" ref={componentRef}>
                    <div ref={mobileRef}>
                        <TransactionCard transaction={transaction}>
                            <TransactionHeader title={getTitleHeader()} />
                            <div className="ticket">
                                <div className="ticket__wrapper">
                                    <div className="ticket__header" />
                                    <TransactionSummaryBody
                                        isTicket
                                        isDesktop={isDesktop}
                                        setShowMoreData={setShowMoreData}
                                        showMoreData={showMoreData}
                                        accounts={accounts}
                                    />
                                    <div className="ticket__footer" data-copytext={i18n.get("global.ticketCopyText")} />
                                </div>
                            </div>
                        </TransactionCard>
                    </div>
                    {!isBackoffice && (
                        <Row className="form-actions no-print">
                            {isCancelled && renderBackToHomeButton(false, 4)}
                            {(isFinished || isFailed) && (
                                <>
                                    {renderBackToHomeButton(false, 2)}
                                    {renderDownloadButton(true, 0)}
                                </>
                            )}
                            {isPending &&
                                (showSignButton ? (
                                    <>
                                        {renderCancelButton(false, 2)}
                                        {renderSignButton(true, 0)}
                                    </>
                                ) : (
                                    <>
                                        {renderBackToHomeButton(false, 2)}
                                        {renderCancelButton(true, 0)}
                                    </>
                                ))}
                            {isFinished && !isDesktop && (
                                <Col xs={12} md={4}>
                                    <Button
                                        className="btn btn-outline"
                                        image="images/figma/icon/share.svg"
                                        label="global.share"
                                        onClick={() => {
                                            handleShareMobile();
                                        }}
                                    />
                                </Col>
                            )}
                        </Row>
                    )}
                </div>
            </MainContainer>
        </>
    );
};

TransactionPage.propTypes = {
    isDesktop: bool.isRequired,
};

export default TransactionPage;
